/**
 * TypeScript mirror of the default pipeline + checklist seed in
 * `supabase/migrations/20260526100400_seed_workspace_default_pipelines.sql`.
 *
 * Used by:
 *   - The Manage Columns "Reset to defaults" button (Phase 2 UI).
 *   - Unit tests that need to assert against the seeded shape.
 *
 * SQL is the source of truth for what gets seeded on workspace creation;
 * keep this file in sync with the migration if either changes.
 */

export interface DefaultChecklistDef {
  text: string;
  type: "check" | "input";
  required?: boolean;
}

export interface DefaultPipelineDef {
  title: string;
  /** Sort order in the kanban (lower = leftmost). */
  order: number;
  /** Hex color for the stage accent dot / column header. */
  color: string;
  checklist: DefaultChecklistDef[];
}

export const DEFAULT_PIPELINES: DefaultPipelineDef[] = [
  { title: "Watchlist",       order: 10, color: "#8a93a3", checklist: [] },
  {
    title: "Analyzing",       order: 20, color: "#3b82f6",
    checklist: [
      { text: "Run financial analysis",   type: "check" },
      { text: "Compare to comps in area", type: "check" },
      { text: "Estimate rehab budget",    type: "check" },
    ],
  },
  {
    title: "Offer Made",      order: 30, color: "#f59e0b",
    checklist: [
      { text: "Offer Amount",                type: "input" },
      { text: "Submit written offer",        type: "check" },
      { text: "Confirm proof of funds sent", type: "check" },
      { text: "Set offer expiration",        type: "check" },
    ],
  },
  {
    title: "Under Contract",  order: 40, color: "#f97316",
    checklist: [
      { text: "EMD Amount",                  type: "input" },
      { text: "Earnest money deposit wired", type: "check" },
      { text: "Schedule inspection",         type: "check" },
    ],
  },
  {
    title: "Due Diligence",   order: 50, color: "#a855f7",
    checklist: [
      { text: "Inspection complete",      type: "check" },
      { text: "Title search ordered",     type: "check" },
      { text: "Review HOA / disclosures", type: "check" },
    ],
  },
  {
    title: "Financing",       order: 60, color: "#06b6d4",
    checklist: [
      { text: "Loan application submitted", type: "check" },
      { text: "Appraisal scheduled",        type: "check" },
    ],
  },
  {
    title: "Closing",         order: 70, color: "#eab308",
    checklist: [
      { text: "Clear-to-close received", type: "check" },
      { text: "Sign closing docs",       type: "check" },
      { text: "Wire down payment",       type: "check" },
    ],
  },
  {
    title: "Closed Won",      order: 80, color: "#16c47a",
    checklist: [
      { text: "Closing costs",     type: "input" },
      { text: "Final loan amount", type: "input" },
    ],
  },
  { title: "Closed Lost",     order: 90, color: "#ef3b3b", checklist: [] },
];

/** Available color swatches for the Manage Columns color popover. */
export const PIPELINE_COLOR_PALETTE = [
  "#8a93a3", "#3b82f6", "#f59e0b", "#f97316", "#a855f7", "#06b6d4",
  "#eab308", "#16c47a", "#ef3b3b", "#ec4899", "#14b8a6", "#84cc16",
] as const;
